import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import io from "socket.io-client";
import ClipLoader from "react-spinners/ClipLoader";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import WebRTCHostClient from "../utils/webrtc/hostClient";
import {
  fetchEventImage,
  fetchSpecificEvent,
  addComment,
  fetchComments,
  clearComments,
  addReaction,
  removeReaction,
  fetchReactionCounts,
  receiveComment,
  receiveReactionAdd,
  receiveReactionRemove,
} from "../redux/slices/userSlice";

const SIGNALING_URL = "wss://telecoms.pluglr.com";
const STREAM_TOKEN_URL =
  "https://apis.pluglr.com/pluglr/api/v1/auth/resolve/stream";

const streamTokenCache = new Map();

const getStreamCacheKey = (channelId, eventId) => {
  if (!channelId || !eventId) return null;
  return `${channelId}:${eventId}`;
};

const base64Encode = (str) => {
  const utf8Bytes = new TextEncoder().encode(str);
  let binary = "";
  utf8Bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const toUrlSafeBase64 = (base64String) =>
  base64String.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const textToUrlSafeBase64 = (text) => {
  const standardBase64 = base64Encode(text);
  return toUrlSafeBase64(standardBase64);
};

const formatTime = (seconds) => {
  const numeric = Number(seconds);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return "0:00";
  }

  const totalSeconds = Math.floor(numeric);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const formatDuration = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "00:00:00";
  }

  const rounded = Math.floor(totalSeconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;

  return [hours, minutes, seconds]
    .map((unit) => unit.toString().padStart(2, "0"))
    .join(":");
};

const formatFileSize = (bytes) => {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "0 MB";
  }

  if (numeric >= 1024 * 1024 * 1024) {
    return `${(numeric / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  if (numeric >= 1024 * 1024) {
    return `${(numeric / (1024 * 1024)).toFixed(2)} MB`;
  }

  if (numeric >= 1024) {
    return `${(numeric / 1024).toFixed(2)} KB`;
  }

  return `${numeric.toFixed(0)} B`;
};

const GoLiveShell = styled.div`
  min-height: 100vh;
  background: #000000;
  color: #eff0fa;
  display: flex;
  justify-content: center;
  padding: 32px 16px 64px;
  box-sizing: border-box;

  .all-the-audios {
    width: min(1180px, 100%);
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: #000000;
  }

  .upper-audio {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding: 24px 32px 0;
  }

  .upper-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .red-rounded {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ed1c24;
  }

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title-block p {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #eff0fa;
  }

  .title-block span {
    font-size: 12px;
    color: #c5c6d0;
  }

  .audio-upper-middle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 100px;
    border: 1px solid #272a31;
    background: #1b1b1b;
  }

  .audio-upper-middle p {
    margin: 0;
    font-weight: 600;
    font-size: 14px;
    color: #eff0fa;
  }

  .cool-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e8e8e8;
    color: #000000;
  }

  .audio-upper-last {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #1f1f1f;
    padding: 10px 16px;
    border-radius: 100px;
    color: #d5d5d5;
    font-size: 12px;
  }

  .middle-audio {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 32px 24px;
  }

  @media (max-width: 768px) {
    .upper-audio {
      padding: 20px 20px 0;
    }

    .middle-audio {
      padding: 0 20px 20px;
    }
  }

  .middle-image-box {
    background: #1b1b1b;
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;
    overflow: hidden;
    width: 100%;
  }

  .middle-image-box.with-controls {
    flex-direction: row;
    align-items: stretch;
    gap: 32px;
  }

  .middle-image-box.with-controls .middle-audio-image {
    flex: 1 1 60%;
  }

  .middle-image-box:not(.with-controls) .middle-audio-image {
    width: 100%;
  }

  .controls-column {
    flex: 1 1 40%;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  @media (max-width: 1024px) {
    .middle-image-box.with-controls {
      flex-direction: column;
    }

    .controls-column {
      width: 100%;
    }
  }

  .middle-audio-image {
    flex: 1;
    min-height: 360px;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 47.01%,
        rgba(0, 0, 0, 0.12) 61.35%,
        rgba(0, 0, 0, 0.34) 78.82%,
        #000000 109.43%
      ),
      linear-gradient(0deg, rgba(0, 0, 0, 0.09), rgba(0, 0, 0, 0.09)),
      url("/images/audio-live-bg.png");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 16px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 16px;
  }

  .middle-audio-image.expanded {
    min-height: 520px;
  }

  .image-overlay-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .image-overlay-content h4 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #ffffff;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 14px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.12);
    color: #eff0fa;
    width: fit-content;
  }

  .status-pill.live {
    background: rgba(237, 28, 36, 0.2);
    color: #ff6b72;
  }

  .listners {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #ffffffcc;
    font-size: 14px;
  }

  .down-audio {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #008850;
    padding: 12px 32px 24px;
    border-radius: 0 0 24px 24px;
    gap: 12px;
    flex-wrap: wrap;
  }

  .down-audio p,
  .down-audio span {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: #ffffff;
  }

  .down-audio-middle {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .cohost-live-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #34d399;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .cohost-live-indicator .pulse-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.8);
    animation: cohostPulse 1.2s ease-in-out infinite;
  }

  .cohost-offline {
    font-size: 12px;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  @keyframes cohostPulse {
    0% {
      transform: scale(0.8);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(0.8);
      opacity: 0.7;
    }
  }
`;

const ControlPanel = styled.div`
  background: #1b1b1b;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #eff0fa;
  }

  .meta {
    font-size: 12px;
    color: #8f9099;
  }
`;

const ControlHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  background: ${({ $variant }) =>
    $variant === "error"
      ? "rgba(248, 113, 113, 0.15)"
      : $variant === "success"
      ? "rgba(34, 197, 94, 0.15)"
      : "rgba(59, 130, 246, 0.15)"};
  color: ${({ $variant }) =>
    $variant === "error"
      ? "#fca5a5"
      : $variant === "success"
      ? "#4ade80"
      : "#93c5fd"};
`;

const DeviceRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8f9099;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #3d3d3d;
  background: #2d2d2d;
  color: #eff0fa;
  font-size: 14px;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
`;

const ControlButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  background: ${({ $variant }) =>
    $variant === "secondary"
      ? "rgba(255, 255, 255, 0.08)"
      : $variant === "tertiary"
      ? "#2d2d2d"
      : "#ed1c24"};
  color: ${({ $variant }) =>
    $variant === "secondary" || $variant === "tertiary"
      ? "#eff0fa"
      : "#ffffff"};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: transform 0.15s ease, background 0.2s ease;

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
  }
`;

const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 999px;
  background: #2d2d2d;
  border: 1px dashed #3d3d3d;
  color: #eff0fa;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  input {
    display: none;
  }
`;

const TrackRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  border-radius: 12px;
  background: #2d2d2d;
  font-size: 14px;

  span {
    flex: 1;
    color: #eff0fa;
  }

  button {
    border: none;
    background: none;
    color: #f53f46;
    font-weight: 600;
    cursor: pointer;
  }
`;

const EmptyState = styled.p`
  font-size: 13px;
  color: #8f9099;
  margin: 0;
`;

const PlaylistWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlaylistActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PlaylistList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 240px;
  overflow-y: auto;
`;

const PlaylistItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ $active }) => ($active ? "#313131" : "#292929")};
  border: ${({ $active }) =>
    $active ? "1px solid #ed1c24" : "1px solid transparent"};
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? "#2f2f2f" : "#303030")};
  }

  .info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .info span {
    color: #eff0fa;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .info small {
    color: #8f9099;
    font-size: 12px;
  }
`;

const PlaylistButtonGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const RepeatToggle = styled.button`
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#ed1c24" : "#2d2d2d")};
  color: #eff0fa;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SmallIconButton = styled.button`
  border: none;
  background: transparent;
  color: #8f9099;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    color: #ff6b72;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  input[type="range"] {
    width: 100%;
    accent-color: #ed1c24;
  }

  .progress-labels {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #8f9099;
  }
`;

const MixerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
`;

const MixerSection = styled.div`
  background: #232323;
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MixerSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #eff0fa;
  }

  small {
    font-size: 12px;
    color: #8f9099;
  }
`;

const SliderRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    flex: 1;
    font-size: 13px;
    color: #c5c6d0;
  }

  input[type="range"] {
    flex: 1.6;
    accent-color: #ed1c24;
  }

  output {
    min-width: 44px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #eff0fa;
    font-size: 13px;
  }
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #eff0fa;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #ed1c24;
  }

  span {
    flex: 1;
  }
`;

const PanelToggleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: #1c1c1c;
`;

const PanelToggleButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#ed1c24" : "transparent")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#c5c6d0")};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#ed1c24" : "#2d2d2d")};
    color: #ffffff;
  }
`;

const ControlContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const LoaderSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #f87171;
  margin: 0;
`;

const CallRequestsPanel = styled.div`
  margin: 16px 32px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #272a31;
  background: rgba(27, 27, 27, 0.92);
  display: flex;
  flex-direction: column;
  gap: 12px;

  .call-requests-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #eff0fa;
    }

    span {
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(237, 28, 36, 0.16);
      color: #ff6b72;
      font-size: 12px;
      font-weight: 600;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #1b1b1b;
    border: 1px solid #2f3035;
  }

  .caller-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
      color: #eff0fa;
      font-size: 14px;
    }

    span {
      font-size: 12px;
      color: #8f9099;
    }
  }

  .caller-actions {
    display: inline-flex;
    gap: 8px;

    button {
      border: none;
      border-radius: 999px;
      padding: 8px 14px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.15s ease, background 0.2s ease;
    }

    button:first-child {
      background: #008850;
      color: #ffffff;
    }

    button:last-child {
      background: rgba(237, 28, 36, 0.12);
      color: #ff6b72;
    }

    button:hover {
      transform: translateY(-1px);
    }
  }
`;

const ShareCard = styled.div`
  background: rgba(27, 27, 27, 0.94);
  border: 1px solid #272a31;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 360px;

  h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
    color: #eff0fa;
  }

  p {
    margin: 0;
    color: #d5d5d5;
    font-size: 13px;
    line-height: 20px;
  }
`;

const ShareLinkRow = styled.div`
  border: 1px solid #444954;
  border-radius: 999px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    flex: 1;
    font-size: 12px;
    color: #eff0fa;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    border: none;
    background: none;
    color: #eff0fa;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const CopyFeedbackNote = styled.span`
  font-size: 12px;
  color: #c5c6d0;
`;

const ControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: #1b1b1b;
  border-radius: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 14px 16px;
    gap: 12px;
  }
`;

const ControlSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  &.center {
    justify-content: center;
    flex: 1;
  }

  &.right {
    margin-left: auto;
  }
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid #272a31;
  background: #232323;
  color: #eff0fa;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    background: ${({ disabled }) => (disabled ? "#232323" : "#2f2f2f")};
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px) ")};
  }

  &:disabled {
    opacity: 0.45;
  }
`;

const CohostActionButton = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${({ disabled }) => (disabled ? "#2f2f2f" : "#2563eb")};
  background: ${({ disabled }) =>
    disabled ? "rgba(255, 255, 255, 0.04)" : "rgba(37, 99, 235, 0.15)"};
  color: ${({ disabled }) => (disabled ? "#9ca3af" : "#bfdbfe")};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${({ disabled }) =>
      disabled ? "rgba(255, 255, 255, 0.04)" : "rgba(37, 99, 235, 0.25)"};
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
  }
`;

const SharePanelWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 32px;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 20px;
  }
`;

const GoLive = () => {
  const navigate = useNavigate();
  const { channelId, eventId } = useParams();

  const dispatch = useDispatch();
  const {
    eventImages,
    specificEvent,
    loading: userLoading,
    comments,
    commentsLoading,
    commentsHasMore,
    commentsPage,
    userData,
  } = useSelector((state) => state.userData);

  console.log(comments);

  const socketRef = useRef(null);
  const hostClientRef = useRef(null);
  const callRequestsRef = useRef(new Map());
  const mountedRef = useRef(false);
  const initialisedRef = useRef(false);
  const streamTokenPromiseRef = useRef(null);
  const skipNextCleanupRef = useRef(false);
  const playlistInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const liveTimerRef = useRef(null);
  const trackIdRef = useRef(0);
  const isSeekingMusicRef = useRef(false);

  const [status, setStatus] = useState("Preparing");
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("default");
  const [playlist, setPlaylist] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
  const [repeatMode, setRepeatMode] = useState("none"); // none | one | all
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [musicPosition, setMusicPosition] = useState(0);
  const [musicDuration, setMusicDuration] = useState(0);
  const [isSeekingMusic, setIsSeekingMusic] = useState(false);
  const [isLoadingMusic, setIsLoadingMusic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showDevicePicker, setShowDevicePicker] = useState(true);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [showMicMixer, setShowMicMixer] = useState(false);
  const [showMusicMixer, setShowMusicMixer] = useState(false);
  const [showAdvancedMixer, setShowAdvancedMixer] = useState(false);
  const [panelTab, setPanelTab] = useState("participants"); // "participants" | "chat"
  const [commentText, setCommentText] = useState("");
  const chatContainerRef = useRef(null);

  console.log(specificEvent);

  // Fetch comments when chat tab is active
  useEffect(() => {
    if (panelTab === "chat" && specificEvent?.data?.id) {
      if ((comments?.content?.length || 0) === 0) {
        dispatch(fetchComments({ eventId: specificEvent.data.id, page: 0 }));
      }
    }
  }, [panelTab, specificEvent?.data?.id, dispatch, comments?.content?.length]);

  // Handle Scroll for infinite loading
  const handleChatScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when scrolled near bottom (if logic is append to bottom)
    // Or if logic is traditional chat history (scroll to top), logic differs.
    // Based on user request: "scroll to the last one then load more", implies bottom trigger.
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      !commentsLoading &&
      commentsHasMore
    ) {
      dispatch(
        fetchComments({
          eventId: specificEvent.data.id,
          page: commentsPage + 1,
        })
      );
    }
  };

  const handleSendComment = async (e) => {
    if (e) e.preventDefault(); // Handle potential onClick event
    console.log("handleSendComment triggered");

    if (!commentText.trim()) {
      console.log("Empty comment text");
      return;
    }

    if (!specificEvent?.data?.id) {
      console.error("Missing specificEvent ID");
      // toast.error("Event ID missing, cannot send comment."); // Optional: alert user
      return;
    }

    console.log("Dispatching addComment", {
      eventId: specificEvent.data.id,
      text: commentText,
    });

    const result = await dispatch(
      addComment({ eventId: specificEvent.data.id, text: commentText })
    );

    if (!result.error) {
      console.log("Comment added successfully");
      setCommentText("");

      // Emit to socket for real-time broadcast
      if (socketRef.current) {
        // Use result.payload.data if nested, otherwise result.payload
        const socketPayload = result.payload?.data || result.payload;
        socketRef.current.emit("comment", socketPayload, (err) => {
          if (err) console.error("Error broadcasting comment:", err);
          else console.log("Comment broadcasted via socket");
        });
      }

      // Refresh to see new comment immediately
      dispatch(clearComments());
      dispatch(fetchComments({ eventId: specificEvent.data.id, page: 0 }));
    } else {
      console.error("Failed to add comment", result.error);
    }
  };

  const handleReaction = async (commentId, reaction, currentReaction) => {
    try {
      let result;
      if (reaction === "NOT_SET" || reaction === currentReaction) {
        // Explicitly clear or toggle off
        result = await dispatch(removeReaction(commentId)).unwrap();
        if (socketRef.current) {
          const userId = hostUser?.id || hostUser?.data?.id;
          console.log("Golive: emitting reactionRemove", {
            commentId,
            reactionType: currentReaction,
            userId,
            hostUser,
          });
          socketRef.current.emit(
            "reactionRemove",
            { commentId, reactionType: currentReaction, userId },
            (err) => {
              if (err) {
                console.error(
                  "error while broadcasting reactionRemove:",
                  JSON.stringify(err)
                );
              } else {
                console.log("comment broadcasted (reaction removed)");
              }
            }
          );
        }
      } else {
        // Set new reaction (backend should handle removing previous reaction)
        result = await dispatch(addReaction({ commentId, reaction })).unwrap();
        if (socketRef.current) {
          const userId = hostUser?.id || hostUser?.data?.id;
          console.log("Golive: emitting reactionAdd", {
            commentId,
            reactionType: reaction,
            previousReactionType: currentReaction,
            userId,
            hostUser,
          });
          socketRef.current.emit(
            "reactionAdd",
            {
              commentId,
              reactionType: reaction,
              previousReactionType: currentReaction || null,
              userId,
            },
            (err) => {
              if (err) {
                console.error(
                  "error while broadcasting reactionAdd:",
                  JSON.stringify(err)
                );
              } else {
                console.log("comment broadcasted (reaction added)");
              }
            }
          );
        }
      }
    } catch (err) {
      console.error("Failed to handle reaction:", err);
    }
  };

  const [activeControlPanel, setActiveControlPanel] = useState("main");
  const [micAudienceVolume, setMicAudienceVolumeState] = useState(1);
  const [micMonitorVolume, setMicMonitorVolumeState] = useState(1);
  const [micMonitorEnabled, setMicMonitorEnabledState] = useState(true);
  const [musicAudienceVolume, setMusicAudienceVolumeState] = useState(0.6);
  const [musicMonitorVolume, setMusicMonitorVolumeState] = useState(0.6);
  const [musicMonitorEnabled, setMusicMonitorEnabledState] = useState(true);
  const [musicMuted, setMusicMutedState] = useState(false);
  const [monitoringEnabled, setMonitoringEnabledState] = useState(true);
  const [duckingEnabled, setDuckingEnabledState] = useState(true);
  const [duckingStrength, setDuckingStrengthState] = useState(0.15);
  const [monitorDuckingStrength, setMonitorDuckingStrengthState] =
    useState(0.3);
  const [liveStartTime, setLiveStartTime] = useState(null);
  const [liveElapsedSeconds, setLiveElapsedSeconds] = useState(0);
  const [broadcastEnded, setBroadcastEnded] = useState(false);
  const [incomingCalls, setIncomingCalls] = useState([]);
  const [activeCallers, setActiveCallers] = useState([]);
  const [callError, setCallError] = useState(null);
  const [cohostStreams, setCohostStreams] = useState([]);

  const userToken = useMemo(() => localStorage.getItem("pluglrUserToken"), []);
  const userProfile = useMemo(() => {
    const storageKeys = ["pluglrUserDetails", "pluglrUserProfile"];
    for (const key of storageKeys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        if (process.env.NODE_ENV !== "production") {
          console.log(`Raw ${key} from storage`, raw);
        }
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(`Failed to parse ${key} from storage`, err);
        }
      }
    }
    return null;
  }, []);

  const hostUser = useMemo(() => {
    // Priority 1: Redux userData
    if (userData && (userData.id || userData.data?.id)) {
      return userData.data || userData;
    }

    // Priority 2: Local storage userProfile
    if (userProfile && typeof userProfile === "object") {
      return userProfile;
    }

    // Priority 3: Fallback
    return { id: "host-placeholder", name: "Host" };
  }, [userData, userProfile]);

  const channelName = useMemo(() => {
    if (!hostUser || !channelId) {
      return null;
    }

    const matchFromOwned = Array.isArray(hostUser?.ownedChannels)
      ? hostUser.ownedChannels.find((channel) => {
          if (!channel?.id) return false;
          return String(channel.id) === String(channelId);
        })
      : null;

    if (matchFromOwned?.name) {
      return matchFromOwned.name;
    }

    if (Array.isArray(hostUser?.channels)) {
      const match = hostUser.channels.find(
        (channel) => String(channel?.id) === String(channelId)
      );
      if (match?.name) {
        return match.name;
      }
    }

    if (typeof hostUser?.channelName === "string") {
      return hostUser.channelName;
    }

    return null;
  }, [hostUser, channelId]);

  const eventDetails = useMemo(() => {
    if (specificEvent && typeof specificEvent === "object") {
      if (specificEvent?.data && typeof specificEvent.data === "object") {
        return specificEvent.data;
      }
    }
    return null;
  }, [specificEvent]);

  const cohostLiveState = useMemo(() => {
    const map = new Map();
    if (Array.isArray(cohostStreams)) {
      cohostStreams.forEach((stream) => {
        if (!stream || !stream.peerId) return;
        const key = String(stream.peerId);
        map.set(key, {
          isLive: Boolean(stream.isLive),
          muted: Boolean(stream.muted),
        });
      });
    }
    return map;
  }, [cohostStreams]);

  useEffect(() => {
    if (eventId && !eventImages?.[eventId]) {
      dispatch(fetchEventImage(eventId));
    }
    if (eventId && String(specificEvent?.data?.id ?? "") !== String(eventId)) {
      dispatch(fetchSpecificEvent(eventId));
    }
  }, [dispatch, eventId, eventImages, specificEvent]);

  const defaultEventImage = "/images/audio-live-bg.png";
  const eventBackgroundImage = eventImages?.[eventId] ?? defaultEventImage;

  const middleAudioBackground = useMemo(
    () =>
      `linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 47.01%,
        rgba(0, 0, 0, 0.12) 61.35%,
        rgba(0, 0, 0, 0.34) 78.82%,
        #000000 109.43%
      ),
      linear-gradient(0deg, rgba(0, 0, 0, 0.09), rgba(0, 0, 0, 0.09)),
      url('${eventBackgroundImage}')`,
    [eventBackgroundImage]
  );

  const getCohostPeerKey = useCallback((cohost) => {
    if (!cohost || typeof cohost !== "object") return null;
    const candidate =
      cohost.id ??
      cohost._id ??
      cohost.userId ??
      cohost.uid ??
      cohost.user_id ??
      null;
    return candidate !== null && candidate !== undefined
      ? String(candidate)
      : null;
  }, []);

  const applyInitialMixerState = useCallback(() => {
    const client = hostClientRef.current;
    if (!client) return;
    client.setMonitoringEnabled(monitoringEnabled);
    client.setDuckingEnabled(duckingEnabled);
    client.setDuckingStrength(duckingStrength, {
      monitorResidual: monitorDuckingStrength,
    });
    client.setMicAudienceVolume(micAudienceVolume);
    client.setMicMonitorVolume(micMonitorVolume);
    client.setMicMonitorEnabled(micMonitorEnabled);
    client.setMicMuted(micMuted);
    client.setMusicAudienceVolume(musicAudienceVolume);
    client.setMusicMonitorVolume(musicMonitorVolume);
    client.setMusicMonitorEnabled(musicMonitorEnabled);
    client.setFileMuted(musicMuted);
  }, [
    duckingEnabled,
    micAudienceVolume,
    micMonitorEnabled,
    micMonitorVolume,
    micMuted,
    monitoringEnabled,
    musicAudienceVolume,
    musicMonitorEnabled,
    musicMonitorVolume,
    musicMuted,
  ]);

  const clearSession = useCallback(
    ({ resetState = true, resetToken = true } = {}) => {
      initialisedRef.current = false;

      if (liveTimerRef.current) {
        clearInterval(liveTimerRef.current);
        liveTimerRef.current = null;
      }

      if (resetToken) {
        streamTokenPromiseRef.current = null;
      }

      if (resetState) {
        setIsStreaming(false);
        setStatus("Idle");
        setMicMuted(false);
        setShareLink(null);
        setCopyFeedback(null);
        setPlaylist([]);
        setCurrentTrackIndex(-1);
        setRepeatMode("none");
        setIsMusicPlaying(false);
        setIsLoadingMusic(false);
        setMusicPosition(0);
        setMusicDuration(0);
        setLiveStartTime(null);
        setLiveElapsedSeconds(0);
        setBroadcastEnded(false);
      }

      const hostClient = hostClientRef.current;
      if (hostClient) {
        hostClientRef.current = null;
        hostClient.cleanup().catch((err) => {
          console.warn("cleanup error", err);
        });
      }

      const socket = socketRef.current;
      if (socket) {
        socketRef.current = null;
        socket.disconnect();
      }
    },
    []
  );

  const handleBeforeUnload = useCallback(() => {
    clearSession({ resetState: false, resetToken: false });
  }, [clearSession]);

  const fetchDevices = useCallback(async () => {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = mediaDevices.filter((d) => d.kind === "audioinput");
      setDevices(audioInputs);
    } catch (err) {
      console.warn("enumerateDevices", err);
    }
  }, []);

  const resolveStreamToken = useCallback(() => {
    if (streamTokenPromiseRef.current) {
      return streamTokenPromiseRef.current;
    }

    const cacheKey = getStreamCacheKey(channelId, eventId);
    if (!cacheKey) {
      throw new Error("Missing channel or event information");
    }

    const cachedEntry = streamTokenCache.get(cacheKey);
    if (cachedEntry?.token) {
      return Promise.resolve(cachedEntry.token);
    }
    if (cachedEntry?.promise) {
      streamTokenPromiseRef.current = cachedEntry.promise;
      return cachedEntry.promise;
    }

    const promise = (async () => {
      const response = await fetch(
        `${STREAM_TOKEN_URL}/${channelId}/${eventId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${userToken}`,
            "X-From": "web",
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message = payload?.message || "Unable to resolve stream token";
        streamTokenPromiseRef.current = null;
        streamTokenCache.delete(cacheKey);
        throw new Error(message);
      }

      const payload = await response.json();
      console.log(payload);

      if (!payload?.data?.token) {
        streamTokenPromiseRef.current = null;
        streamTokenCache.delete(cacheKey);
        throw new Error("Invalid token response from server");
      }

      const token = payload.data.token;
      streamTokenCache.set(cacheKey, { token });
      return token;
    })().catch((error) => {
      streamTokenPromiseRef.current = null;
      throw error;
    });

    streamTokenPromiseRef.current = promise;
    streamTokenCache.set(cacheKey, { promise });
    return promise;
  }, [channelId, eventId, userToken]);

  const connectSocket = useCallback(async (token) => {
    return new Promise((resolve, reject) => {
      try {
        const socket = io(SIGNALING_URL, {
          path: "/socket.io",
          transports: ["websocket"],
          query: { token, channelId, eventId, role: "host" },
        });

        socket.once("connect", () => {
          resolve(socket);
        });

        socket.once("connect_error", (err) => {
          reject(err || new Error("Socket connection failed"));
        });

        socket.once("disconnect", () => {
          setStatus("Socket disconnected");
        });

        socketRef.current = socket;
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  const handleIncomingCall = useCallback((call) => {
    if (!call || !call.requesterSocketId) return;

    callRequestsRef.current.set(call.requesterSocketId, call);
    setIncomingCalls((prev) => {
      const existing = prev.filter(
        (item) => item.requesterSocketId !== call.requesterSocketId
      );
      return [...existing, call].sort(
        (a, b) => (a.requestedAt || 0) - (b.requestedAt || 0)
      );
    });
  }, []);

  const handleCallerStateChange = useCallback((event) => {
    const { callerId, status, message } = event || {};
    if (!callerId) return;

    if (
      status === "accepted" ||
      status === "promoted" ||
      status === "connected"
    ) {
      setIncomingCalls((prev) =>
        prev.filter((item) => item.requesterSocketId !== callerId)
      );
      callRequestsRef.current.delete(callerId);
    } else if (status === "rejected") {
      setIncomingCalls((prev) =>
        prev.filter((item) => item.requesterSocketId !== callerId)
      );
      callRequestsRef.current.delete(callerId);
    } else if (status === "error") {
      setCallError(message || "Call request failed");
    }
  }, []);

  const handleActiveCallersChange = useCallback((callers) => {
    setActiveCallers(Array.isArray(callers) ? callers : []);
  }, []);

  const handleCohostStreamsChange = useCallback((streams) => {
    setCohostStreams(Array.isArray(streams) ? streams : []);
  }, []);

  const acceptCallRequest = useCallback(async (callerSocketId) => {
    if (!callerSocketId || !hostClientRef.current) return;
    setCallError(null);
    try {
      await hostClientRef.current.acceptCaller(callerSocketId);
    } catch (err) {
      console.error("acceptCaller", err);
      const message = err instanceof Error ? err.message : String(err);
      setCallError(message);
    }
  }, []);

  const rejectCallRequest = useCallback(async (callerSocketId) => {
    if (!callerSocketId || !hostClientRef.current) return;
    setCallError(null);
    try {
      await hostClientRef.current.rejectCaller(callerSocketId);
    } catch (err) {
      console.error("rejectCaller", err);
      const message = err instanceof Error ? err.message : String(err);
      setCallError(message);
    }
  }, []);

  const endActiveCaller = useCallback(async (callerSocketId) => {
    if (!callerSocketId || !hostClientRef.current) return;
    setCallError(null);
    try {
      await hostClientRef.current.endCaller(callerSocketId);
    } catch (err) {
      console.error("endCaller", err);
      const message = err instanceof Error ? err.message : String(err);
      setCallError(message);
    }
  }, []);

  const ensureHostClient = useCallback(
    async (socket) => {
      if (hostClientRef.current) {
        return hostClientRef.current;
      }

      const hostClient = new WebRTCHostClient({
        socket,
        channelId,
        eventId,
        user: hostUser,
        cohosts: specificEvent?.data?.cohosts,
        onStatusChange: (message) => setStatus(message),
        onError: (err) => {
          console.error("Host error", err);
          setError(err instanceof Error ? err.message : String(err));
        },
        onIncomingCall: handleIncomingCall,
        onCallerStateChange: handleCallerStateChange,
        onActiveCallersChange: handleActiveCallersChange,
        onCohostStreamsChange: handleCohostStreamsChange,
      });

      socket.on("readyToBroadcast", () => {
        setStatus("Ready to start");
      });

      socket.on("caller-left", ({ callerId }) => {
        if (!callerId) return;
        callRequestsRef.current.delete(callerId);
        setIncomingCalls((prev) =>
          prev.filter((item) => item.requesterSocketId !== callerId)
        );
      });

      // Real-time Chat & Reactions
      socket.on("onComment", (comment) => {
        console.log("Received real-time comment:", comment);
        dispatch(receiveComment(comment));
      });

      socket.on("onReactionAdd", (data) => {
        console.log("Received real-time reactionAdd:", data);
        dispatch(receiveReactionAdd(data));
      });

      socket.on("onReactionRemove", (data) => {
        console.log("Received real-time reactionRemove:", data);
        dispatch(receiveReactionRemove(data));
      });

      hostClientRef.current = hostClient;
      return hostClient;
    },
    [
      channelId,
      eventId,
      handleCallerStateChange,
      handleIncomingCall,
      hostUser,
      handleActiveCallersChange,
      handleCohostStreamsChange,
      specificEvent,
    ]
  );

  const buildShareLink = useCallback(() => {
    if (!channelId || !eventId) {
      return null;
    }

    const streamObj = {
      jwt: "anon",
      channelId,
      eventId,
      name: channelName || hostUser?.fname || "Channel",
      title: channelName ? `${channelName} Live` : "Live stream",
    };

    const payload = JSON.stringify(streamObj);
    const coded = textToUrlSafeBase64(payload);
    // return `https://telecoms.pluglr.com/anon.html?opts=${coded}`
    // return `http://localhost:8080/non-user-listner?opts=${coded}`;
    return `https://pluglr.com/non-user-listner?opts=${coded}`;
  }, [channelId, channelName, eventId, hostUser]);

  const notifyEventLifecycle = useCallback(
    async (action) => {
      if (!eventId) {
        throw new Error("Missing event ID");
      }
      if (!userToken) {
        throw new Error("Missing authorization token");
      }

      const baseUrl = "https://apis.pluglr.com/pluglr/api/v1/auth/events";
      const endpoint =
        action === "start"
          ? `${baseUrl}/start/${eventId}`
          : `${baseUrl}/stop/${eventId}`;

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${userToken}`,
          "X-From": "web",
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const defaultMessage =
          action === "start" ? "Failed to start event" : "Failed to stop event";
        throw new Error(payload?.message || defaultMessage);
      }

      return response.json().catch(() => null);
    },
    [eventId, userToken]
  );

  const handleCopyLink = useCallback(async () => {
    if (!shareLink) return;

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopyFeedback("Link copied!");
    } catch (err) {
      console.warn("clipboard write failed", err);
      setCopyFeedback("Copy failed. Please copy manually.");
    }
  }, [shareLink]);

  const initialiseStream = useCallback(async () => {
    if (initialisedRef.current) {
      return;
    }

    initialisedRef.current = true;
    setLoading(true);
    setError(null);
    setStatus("Resolving access");

    try {
      const token = await resolveStreamToken();
      const socket = await connectSocket(token);
      const hostClient = await ensureHostClient(socket);
      await hostClient.prepare();
      applyInitialMixerState();
      await fetchDevices();
      setMicMuted(false);
      const link = buildShareLink();
      setShareLink(link);
      setStatus("Ready to start");
    } catch (err) {
      console.error("initialiseStream", err);
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setStatus("Initialization failed");
      initialisedRef.current = false;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    connectSocket,
    ensureHostClient,
    fetchDevices,
    resolveStreamToken,
    buildShareLink,
  ]);

  const handleStartStreaming = useCallback(async () => {
    if (!hostClientRef.current || isStreaming) return;

    if (broadcastEnded) {
      window.alert("Broadcast has ended.");
      return;
    }

    setIsStarting(true);
    setError(null);
    setStatus("Starting broadcast");

    let backendStarted = false;
    try {
      if (selectedDevice && selectedDevice !== "default") {
        hostClientRef.current.setAudioInputDevice(selectedDevice);
      }
      await notifyEventLifecycle("start");
      backendStarted = true;
      await hostClientRef.current.startBroadcast();
      setIsStreaming(true);
      setMicMuted(false);
      setStatus("Live");
      const now = Date.now();
      setLiveStartTime(now);
      setLiveElapsedSeconds(0);
      setBroadcastEnded(false);
    } catch (err) {
      if (backendStarted) {
        await notifyEventLifecycle("stop").catch((rollbackError) => {
          console.warn("Failed to rollback start notification", rollbackError);
        });
      }
      console.error("handleStartStreaming", err);
      setError(err instanceof Error ? err.message : String(err));
      setStatus("Failed to start");
    } finally {
      setIsStarting(false);
    }
  }, [isStreaming, notifyEventLifecycle, selectedDevice, broadcastEnded]);

  const handleStopStreaming = useCallback(async () => {
    if (!hostClientRef.current) return;
    setStatus("Stopping stream");
    setError(null);
    try {
      await hostClientRef.current.stopBroadcast();
      setIsStreaming(false);
      setMicMuted(false);
      await notifyEventLifecycle("stop");
      setStatus("Stopped");
      const endedAt = Date.now();
      setLiveElapsedSeconds((prev) => {
        if (liveStartTime) {
          const calculated = Math.floor((endedAt - liveStartTime) / 1000);
          return Number.isFinite(calculated)
            ? Math.max(calculated, prev)
            : prev;
        }
        return prev;
      });
      setLiveStartTime(null);
      setBroadcastEnded(true);
    } catch (err) {
      console.error("handleStopStreaming", err);
      setError(err instanceof Error ? err.message : String(err));
      setStatus("Failed to stop");
    }
  }, [notifyEventLifecycle, liveStartTime]);

  useEffect(() => {
    if (isStreaming && liveStartTime) {
      const updateElapsed = () => {
        setLiveElapsedSeconds(Math.floor((Date.now() - liveStartTime) / 1000));
      };
      updateElapsed();
      liveTimerRef.current = setInterval(updateElapsed, 1000);
      return () => {
        if (liveTimerRef.current) {
          clearInterval(liveTimerRef.current);
          liveTimerRef.current = null;
        }
      };
    }

    if (liveTimerRef.current) {
      clearInterval(liveTimerRef.current);
      liveTimerRef.current = null;
    }

    return undefined;
  }, [isStreaming, liveStartTime]);

  const handleToggleMic = useCallback(() => {
    if (!hostClientRef.current) return;
    const next = !micMuted;
    hostClientRef.current.setMicMuted(next);
    setMicMuted(next);
    setStatus(
      next ? "Microphone muted" : isStreaming ? "Live" : "Ready to start"
    );
  }, [isStreaming, micMuted]);

  const handleToggleMusicMute = useCallback(() => {
    if (!hostClientRef.current) return;
    const next = !musicMuted;
    hostClientRef.current.setFileMuted(next);
    setMusicMutedState(next);
  }, [musicMuted]);

  const handleToggleCohostMute = useCallback((peerId) => {
    if (!peerId || !hostClientRef.current) return;
    hostClientRef.current.toggleCohostMute(peerId);
  }, []);

  const handleMicAudienceVolumeChange = useCallback((value) => {
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return;
    setMicAudienceVolumeState(numeric);
    hostClientRef.current?.setMicAudienceVolume(numeric);
  }, []);

  const handleMicMonitorVolumeChange = useCallback((value) => {
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return;
    setMicMonitorVolumeState(numeric);
    hostClientRef.current?.setMicMonitorVolume(numeric);
  }, []);

  const handleMusicAudienceVolumeChange = useCallback((value) => {
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return;
    setMusicAudienceVolumeState(numeric);
    hostClientRef.current?.setMusicAudienceVolume(numeric);
  }, []);

  const handleMusicMonitorVolumeChange = useCallback((value) => {
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return;
    setMusicMonitorVolumeState(numeric);
    hostClientRef.current?.setMusicMonitorVolume(numeric);
  }, []);

  const handleMicMonitorToggle = useCallback((enabled) => {
    const next = Boolean(enabled);
    setMicMonitorEnabledState(next);
    hostClientRef.current?.setMicMonitorEnabled(next);
  }, []);

  const handleMusicMonitorToggle = useCallback((enabled) => {
    const next = Boolean(enabled);
    setMusicMonitorEnabledState(next);
    hostClientRef.current?.setMusicMonitorEnabled(next);
  }, []);

  const handleMonitoringToggle = useCallback((enabled) => {
    const next = Boolean(enabled);
    setMonitoringEnabledState(next);
    hostClientRef.current?.setMonitoringEnabled(next);
  }, []);

  const handleDuckingToggle = useCallback((enabled) => {
    const next = Boolean(enabled);
    setDuckingEnabledState(next);
    hostClientRef.current?.setDuckingEnabled(next);
  }, []);

  const handleDuckingStrengthChange = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;
      const clamped = Math.max(0, Math.min(numeric, 1));
      setDuckingStrengthState(clamped);
      hostClientRef.current?.setDuckingStrength(clamped, {
        monitorResidual: monitorDuckingStrength,
      });
    },
    [monitorDuckingStrength]
  );

  const handleMonitorDuckingStrengthChange = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;
      const clamped = Math.max(0, Math.min(numeric, 1));
      setMonitorDuckingStrengthState(clamped);
      hostClientRef.current?.setDuckingStrength(duckingStrength, {
        monitorResidual: clamped,
      });
    },
    [duckingStrength]
  );

  const cleanupProgressTimer = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const syncPlaybackState = useCallback(() => {
    const client = hostClientRef.current;
    if (!client || typeof client.getMusicPlaybackState !== "function") {
      return;
    }

    const state = client.getMusicPlaybackState();
    if (!state) {
      return;
    }

    setMusicDuration(state.duration || 0);
    if (!isSeekingMusicRef.current) {
      setMusicPosition(state.position || 0);
    }
    setIsMusicPlaying(Boolean(state.isPlaying));
  }, []);

  const startProgressTimer = useCallback(() => {
    cleanupProgressTimer();
    progressIntervalRef.current = window.setInterval(() => {
      syncPlaybackState();
    }, 500);
  }, [cleanupProgressTimer, syncPlaybackState]);

  const resetMusicState = useCallback(() => {
    cleanupProgressTimer();
    setIsMusicPlaying(false);
    setIsLoadingMusic(false);
    setMusicPosition(0);
    setMusicDuration(0);
  }, [cleanupProgressTimer]);

  const handlePlaylistFilesSelected = useCallback((event) => {
    const fileList = event.target.files;
    const files = fileList ? Array.from(fileList) : [];
    event.target.value = "";
    if (!files.length) return;

    setPlaylist((prev) => {
      const createdTracks = files.map((file) => {
        trackIdRef.current += 1;
        return {
          id: trackIdRef.current,
          file,
          name: file.name,
          size: file.size,
          duration: null,
        };
      });

      const next = [...prev, ...createdTracks];
      if (prev.length === 0 && createdTracks.length > 0) {
        setCurrentTrackIndex(0);
      }
      return next;
    });
  }, []);

  const playTrack = useCallback(
    async (index, { autoPlay = true } = {}) => {
      if (index < 0 || index >= playlist.length) {
        return;
      }

      const client = hostClientRef.current;
      if (!client) {
        return;
      }

      const track = playlist[index];
      setError(null);
      setIsLoadingMusic(true);
      setCurrentTrackIndex(index);

      try {
        const info = autoPlay
          ? await client.playMusicFile(track.file, {
              loop: repeatMode === "one",
            })
          : await client.loadMusicFile(track.file);

        const duration = info?.duration || track.duration || 0;
        setMusicDuration(duration);
        setMusicPosition(0);
        setIsMusicPlaying(Boolean(autoPlay));
        setIsLoadingMusic(false);

        if (autoPlay) {
          startProgressTimer();
        } else {
          syncPlaybackState();
        }

        setPlaylist((prev) =>
          prev.map((item) =>
            item.id === track.id
              ? { ...item, duration: duration || item.duration }
              : item
          )
        );
      } catch (err) {
        console.error("playTrack", err);
        setIsLoadingMusic(false);
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [playlist, repeatMode, startProgressTimer, syncPlaybackState]
  );

  const handleMusicPlayPause = useCallback(async () => {
    const client = hostClientRef.current;
    if (!client || !playlist.length) {
      return;
    }

    if (isMusicPlaying) {
      client.pauseMusic();
      cleanupProgressTimer();
      setIsMusicPlaying(false);
      return;
    }

    if (currentTrackIndex === -1) {
      await playTrack(0, { autoPlay: true });
      return;
    }

    const resumed = client.resumeMusic({ loop: repeatMode === "one" });
    if (resumed) {
      setIsMusicPlaying(true);
      startProgressTimer();
      return;
    }

    await playTrack(currentTrackIndex >= 0 ? currentTrackIndex : 0, {
      autoPlay: true,
    });
  }, [
    cleanupProgressTimer,
    currentTrackIndex,
    isMusicPlaying,
    playTrack,
    playlist.length,
    repeatMode,
    startProgressTimer,
  ]);

  const handleStopMusic = useCallback(() => {
    const client = hostClientRef.current;
    if (!client) return;

    client.stopMusic({ resetPosition: true });
    resetMusicState();
  }, [resetMusicState]);

  const handlePlayNextTrack = useCallback(async () => {
    if (!playlist.length) {
      return;
    }

    if (currentTrackIndex === -1) {
      await playTrack(0, { autoPlay: true });
      return;
    }

    let nextIndex = currentTrackIndex + 1;
    if (nextIndex >= playlist.length) {
      if (repeatMode === "all") {
        nextIndex = 0;
      } else {
        handleStopMusic();
        setCurrentTrackIndex(-1);
        return;
      }
    }

    await playTrack(nextIndex, { autoPlay: true });
  }, [
    currentTrackIndex,
    handleStopMusic,
    playTrack,
    playlist.length,
    repeatMode,
  ]);

  const handlePlayPreviousTrack = useCallback(async () => {
    if (!playlist.length) {
      return;
    }

    if (currentTrackIndex <= 0) {
      if (repeatMode === "all" && playlist.length > 0) {
        await playTrack(playlist.length - 1, { autoPlay: true });
        return;
      }

      await playTrack(0, { autoPlay: true });
      return;
    }

    await playTrack(currentTrackIndex - 1, { autoPlay: true });
  }, [currentTrackIndex, playTrack, playlist.length, repeatMode]);

  const handleRepeatToggle = useCallback(() => {
    setRepeatMode((prev) => {
      if (prev === "none") return "all";
      if (prev === "all") return "one";
      return "none";
    });
  }, []);

  const handlePlaylistItemClick = useCallback(
    (index) => {
      if (index < 0 || index >= playlist.length) return;
      playTrack(index, { autoPlay: true });
    },
    [playTrack, playlist.length]
  );

  const handleRemoveTrack = useCallback(
    (trackId) => {
      if (!playlist.length) return;

      const index = playlist.findIndex((item) => item.id === trackId);
      if (index === -1) return;

      const nextPlaylist = playlist.filter((item) => item.id !== trackId);
      setPlaylist(nextPlaylist);

      if (index === currentTrackIndex) {
        handleStopMusic();
        if (nextPlaylist.length > 0) {
          setCurrentTrackIndex(Math.min(index, nextPlaylist.length - 1));
        } else {
          setCurrentTrackIndex(-1);
        }
      } else if (index < currentTrackIndex) {
        setCurrentTrackIndex((prev) => Math.max(0, prev - 1));
      }
    },
    [currentTrackIndex, handleStopMusic, playlist]
  );

  const handleClearPlaylist = useCallback(() => {
    if (!playlist.length) return;
    setPlaylist([]);
    setCurrentTrackIndex(-1);
    handleStopMusic();
  }, [handleStopMusic, playlist.length]);

  const handleMusicSeekStart = useCallback(() => {
    isSeekingMusicRef.current = true;
    setIsSeekingMusic(true);
    cleanupProgressTimer();
  }, [cleanupProgressTimer]);

  const handleMusicSeekChange = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    setMusicPosition(numeric);
  }, []);

  const handleMusicSeekCommit = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;

      isSeekingMusicRef.current = false;
      setIsSeekingMusic(false);

      const client = hostClientRef.current;
      if (!client) return;

      client.seekMusic(numeric, { autoPlay: isMusicPlaying });
      if (isMusicPlaying) {
        startProgressTimer();
      } else {
        syncPlaybackState();
      }
    },
    [isMusicPlaying, startProgressTimer, syncPlaybackState]
  );

  const handleMusicEnded = useCallback(() => {
    cleanupProgressTimer();
    setIsMusicPlaying(false);
    setMusicPosition((prev) => (musicDuration > 0 ? musicDuration : prev));

    if (!playlist.length) {
      return;
    }

    if (repeatMode === "one") {
      const client = hostClientRef.current;
      if (client) {
        client.seekMusic(0, { autoPlay: true });
        setMusicPosition(0);
        setIsMusicPlaying(true);
        startProgressTimer();
      }
      return;
    }

    let nextIndex = currentTrackIndex + 1;
    if (nextIndex >= playlist.length) {
      if (repeatMode === "all" && playlist.length > 0) {
        nextIndex = 0;
      } else {
        setCurrentTrackIndex(-1);
        return;
      }
    }

    playTrack(nextIndex, { autoPlay: true });
  }, [
    cleanupProgressTimer,
    currentTrackIndex,
    musicDuration,
    playlist,
    playTrack,
    repeatMode,
    startProgressTimer,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    skipNextCleanupRef.current = true;
    const allowCleanupTimer = setTimeout(() => {
      skipNextCleanupRef.current = false;
    }, 0);

    let loadListenerAttached = false;
    const shouldInitialise = Boolean(
      channelId && eventId && userToken && specificEvent?.data
    );

    if (!channelId || !eventId) {
      setError("Missing channel or event context");
      setStatus("Cannot initialize");
      setLoading(false);
    } else if (!userToken) {
      setError("You must be logged in to start a broadcast");
      setStatus("Unauthorized");
      setLoading(false);
      navigate("/login");
    }

    const startInit = () => {
      if (!mountedRef.current || !shouldInitialise) {
        return;
      }
      initialiseStream();
    };

    if (shouldInitialise) {
      if (document.readyState === "complete") {
        startInit();
      } else {
        window.addEventListener("load", startInit, { once: true });
        loadListenerAttached = true;
      }

      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      clearTimeout(allowCleanupTimer);

      if (loadListenerAttached) {
        window.removeEventListener("load", startInit);
      }

      if (shouldInitialise) {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      }

      mountedRef.current = false;

      if (skipNextCleanupRef.current) {
        skipNextCleanupRef.current = false;
        return;
      }

      clearSession();
    };
  }, [
    channelId,
    eventId,
    clearSession,
    handleBeforeUnload,
    initialiseStream,
    navigate,
    userToken,
    specificEvent,
  ]);

  const liveStatusVariant = useMemo(() => {
    if (error) return "error";
    if (isStreaming) return "success";
    return "info";
  }, [error, isStreaming]);

  function timeAgo(timestamp) {
    const diff = Date.now() - timestamp;

    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;

    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }

  return (
    <GoLiveShell>
      <div className="all-the-audios">
        <div className="upper-audio">
          <div className="upper-left">
            <div className="red-rounded" />
            <div className="title-block">
              <p>
                {channelName || hostUser?.fname || "Live"}
                {eventDetails?.title ? ` · ${eventDetails.title}` : ""}
              </p>
              {/* <span>
                Event ID: <strong>{eventId || "–"}</strong> · Channel ID: <strong>{channelId || "–"}</strong>
              </span> */}
            </div>
          </div>
          <div className="upper-middle">
            {isStreaming ? (
              <div className="live-duration live">
                {/* <span>Live duration</span> */}
                <strong>{formatDuration(liveElapsedSeconds)}</strong>
              </div>
            ) : broadcastEnded ? (
              <div className="live-duration ended">
                {/* <span>Broadcast ended</span> */}
                <strong>{formatDuration(liveElapsedSeconds)}</strong>
              </div>
            ) : (
              <div className="live-duration idle">
                {/* <span>Ready to go live</span> */}
                <strong>00:00:00</strong>
              </div>
            )}
          </div>
          <div className="upper-right">
            <div className="audio-upper-middle">
              <Icon
                icon="mdi:radio-tower"
                width={20}
                height={20}
                color="#ff6b72"
              />
              <p>{isStreaming ? "Live" : "Ready"}</p>
            </div>
            {/* <div className="audio-upper-last">
              <span>Status:</span>
              <strong>{status}</strong>
            </div> */}
          </div>
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {callError && <ErrorMessage>{callError}</ErrorMessage>}

        {(incomingCalls.length > 0 || activeCallers.length > 0) && (
          <CallRequestsPanel>
            <div className="call-requests-header">
              <h3>Call Management</h3>
            </div>
            {incomingCalls.length > 0 && (
              <div className="call-section">
                <div className="call-section-header">
                  <strong>Incoming Requests</strong>
                  <span>{incomingCalls.length}</span>
                </div>
                <ul>
                  {incomingCalls.map((call) => (
                    <li key={call.requesterSocketId}>
                      <div className="caller-meta">
                        <strong>{call.requesterName || "Listener"}</strong>
                        <span>
                          {call.requestedAt
                            ? new Date(call.requestedAt).toLocaleTimeString()
                            : "Just now"}
                        </span>
                      </div>
                      <div className="caller-actions">
                        <button
                          type="button"
                          onClick={() =>
                            acceptCallRequest(call.requesterSocketId)
                          }
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            rejectCallRequest(call.requesterSocketId)
                          }
                        >
                          Reject
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {activeCallers.length > 0 && (
              <div className="call-section">
                <div className="call-section-header">
                  <strong>Live Callers</strong>
                  <span>{activeCallers.length}</span>
                </div>
                <ul>
                  {activeCallers.map((caller) => (
                    <li key={caller.callerId}>
                      <div className="caller-meta">
                        <strong>{caller.name || "Caller"}</strong>
                        <span>
                          {caller.streamActive ? "On air" : "Connecting"}
                        </span>
                      </div>
                      <div className="caller-actions">
                        <button
                          type="button"
                          onClick={() => endActiveCaller(caller.callerId)}
                        >
                          End call
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CallRequestsPanel>
        )}

        {loading ? (
          <LoaderSection>
            <ClipLoader size={48} color="#ed1c24" />
          </LoaderSection>
        ) : (
          <div>
            <div
              className={`middle-image-box ${
                showControls || showParticipants ? "with-controls" : ""
              }`}
            >
              <div
                className={`middle-audio-image ${
                  showControls || showParticipants ? "" : "expanded"
                }`}
                style={{
                  background: middleAudioBackground,
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                }}
              >
                <span className={`status-pill ${isStreaming ? "live" : ""}`}>
                  {isStreaming ? "Live" : "Preview"}
                </span>
                <div className="image-overlay-content">
                  <h4>
                    {channelName || hostUser?.fname || "Studio"}
                    {eventDetails?.title
                      ? ` · ${eventDetails.title}`
                      : " Preview"}
                  </h4>
                  <div className="listners">
                    <Icon icon="mdi:account-multiple" width={18} height={18} />
                    <span>
                      {isStreaming
                        ? "Audience is listening"
                        : "Invite listeners to join"}
                    </span>
                  </div>
                </div>
              </div>

              {showControls && (
                <div className="controls-column">
                  <div className="panel-toggle-wrapper">
                    <PanelToggleGroup>
                      <PanelToggleButton
                        type="button"
                        $active={activeControlPanel === "main"}
                        onClick={() => setActiveControlPanel("main")}
                      >
                        Main Controls
                      </PanelToggleButton>
                      <PanelToggleButton
                        type="button"
                        $active={activeControlPanel === "music"}
                        onClick={() => setActiveControlPanel("music")}
                      >
                        Music Player
                      </PanelToggleButton>
                      <PanelToggleButton
                        type="button"
                        $active={activeControlPanel === "mixer"}
                        onClick={() => setActiveControlPanel("mixer")}
                      >
                        Mixer Controls
                      </PanelToggleButton>
                    </PanelToggleGroup>
                  </div>
                  <ControlPanel>
                    <ControlHeader>
                      <h2>
                        {activeControlPanel === "main"
                          ? "Studio Controls"
                          : activeControlPanel === "music"
                          ? "Music Player"
                          : "Mixer Controls"}
                      </h2>
                      <StatusChip $variant={liveStatusVariant}>
                        <Icon
                          icon={
                            error
                              ? "mdi:alert-circle"
                              : isStreaming
                              ? "mdi:radio"
                              : "mdi:progress-clock"
                          }
                          width={18}
                          height={18}
                        />
                        {status}
                      </StatusChip>
                    </ControlHeader>
                    <p className="meta">
                      {activeControlPanel === "main"
                        ? "Fine-tune your setup before going live to your audience."
                        : activeControlPanel === "music"
                        ? "Manage your playlist, transport, and repeat settings."
                        : "Adjust your mix to balance microphone, music, and monitoring."}
                    </p>

                    <ControlContent>
                      {activeControlPanel === "main" && (
                        <>
                          {showDevicePicker && (
                            <DeviceRow>
                              <label htmlFor="device-select">
                                Microphone Source
                              </label>
                              <StyledSelect
                                id="device-select"
                                value={selectedDevice}
                                onChange={(event) =>
                                  setSelectedDevice(event.target.value)
                                }
                              >
                                <option value="default">
                                  Default microphone
                                </option>
                                {devices.map((device) => (
                                  <option
                                    key={device.deviceId || device.label}
                                    value={device.deviceId || "default"}
                                  >
                                    {device.label || "Unnamed device"}
                                  </option>
                                ))}
                              </StyledSelect>
                            </DeviceRow>
                          )}

                          <ButtonGrid>
                            {!isStreaming && (
                              <ControlButton
                                onClick={handleStartStreaming}
                                disabled={isStarting}
                              >
                                {isStarting ? (
                                  <>
                                    <ClipLoader size={16} color="#ffffff" />
                                    Starting…
                                  </>
                                ) : (
                                  <>
                                    <Icon
                                      icon="mdi:radio-tower"
                                      width={18}
                                      height={18}
                                    />
                                    Go live
                                  </>
                                )}
                              </ControlButton>
                            )}

                            {isStreaming && (
                              <ControlButton
                                $variant="secondary"
                                onClick={handleStopStreaming}
                              >
                                <Icon
                                  icon="mdi:stop-circle-outline"
                                  width={18}
                                  height={18}
                                />
                                Stop broadcast
                              </ControlButton>
                            )}

                            <ControlButton
                              $variant="tertiary"
                              onClick={fetchDevices}
                              disabled={loading}
                            >
                              <Icon icon="mdi:refresh" width={18} height={18} />
                              Refresh devices
                            </ControlButton>

                            <ControlButton
                              $variant="tertiary"
                              onClick={handleToggleMic}
                              disabled={!hostClientRef.current}
                            >
                              <Icon
                                icon={
                                  micMuted
                                    ? "mdi:microphone-off"
                                    : "mdi:microphone"
                                }
                                width={18}
                                height={18}
                              />
                              {micMuted ? "Unmute mic" : "Mute mic"}
                            </ControlButton>

                            <ControlButton
                              $variant="secondary"
                              onClick={handleCopyLink}
                              disabled={!shareLink}
                            >
                              <Icon
                                icon="mdi:link-variant"
                                width={18}
                                height={18}
                              />
                              Copy stream link
                            </ControlButton>
                          </ButtonGrid>
                        </>
                      )}

                      {activeControlPanel === "music" && (
                        <PlaylistWrapper>
                          <PlaylistActions>
                            <UploadLabel as="label">
                              <Icon
                                icon="mdi:music-note-plus"
                                width={18}
                                height={18}
                              />
                              Add tracks
                              <input
                                ref={playlistInputRef}
                                type="file"
                                accept="audio/*"
                                multiple
                                onChange={handlePlaylistFilesSelected}
                              />
                            </UploadLabel>
                            <PlaylistButtonGroup>
                              <ControlButton
                                $variant="secondary"
                                onClick={handlePlayPreviousTrack}
                                disabled={playlist.length === 0}
                              >
                                <Icon
                                  icon="mdi:skip-previous"
                                  width={18}
                                  height={18}
                                />
                                Prev
                              </ControlButton>
                              <ControlButton
                                onClick={handleMusicPlayPause}
                                disabled={
                                  playlist.length === 0 || isLoadingMusic
                                }
                              >
                                <Icon
                                  icon={
                                    isMusicPlaying
                                      ? "mdi:pause-circle"
                                      : "mdi:play-circle"
                                  }
                                  width={20}
                                  height={20}
                                />
                                {isMusicPlaying ? "Pause" : "Play"}
                              </ControlButton>
                              <ControlButton
                                $variant="secondary"
                                onClick={handlePlayNextTrack}
                                disabled={playlist.length === 0}
                              >
                                <Icon
                                  icon="mdi:skip-next"
                                  width={18}
                                  height={18}
                                />
                                Next
                              </ControlButton>
                              <ControlButton
                                $variant="tertiary"
                                onClick={handleStopMusic}
                                disabled={playlist.length === 0}
                              >
                                <Icon
                                  icon="mdi:stop-circle"
                                  width={18}
                                  height={18}
                                />
                                Stop
                              </ControlButton>
                            </PlaylistButtonGroup>
                            <RepeatToggle
                              type="button"
                              onClick={handleRepeatToggle}
                              $active={repeatMode !== "none"}
                            >
                              <Icon
                                icon={
                                  repeatMode === "one"
                                    ? "mdi:repeat-once"
                                    : repeatMode === "all"
                                    ? "mdi:repeat-variant"
                                    : "mdi:repeat"
                                }
                                width={18}
                                height={18}
                              />
                              {repeatMode === "one"
                                ? "Repeat one"
                                : repeatMode === "all"
                                ? "Repeat all"
                                : "Repeat off"}
                            </RepeatToggle>
                            <ControlButton
                              $variant="tertiary"
                              onClick={handleClearPlaylist}
                              disabled={playlist.length === 0}
                            >
                              <Icon
                                icon="mdi:playlist-remove"
                                width={18}
                                height={18}
                              />
                              Clear playlist
                            </ControlButton>
                          </PlaylistActions>

                          <ProgressContainer>
                            <input
                              type="range"
                              min={0}
                              max={musicDuration || 0}
                              value={musicPosition}
                              disabled={playlist.length === 0}
                              onMouseDown={handleMusicSeekStart}
                              onTouchStart={handleMusicSeekStart}
                              onChange={(event) =>
                                handleMusicSeekChange(event.target.value)
                              }
                              onMouseUp={handleMusicSeekCommit}
                              onTouchEnd={handleMusicSeekCommit}
                            />
                            <div className="progress-labels">
                              <span>{formatTime(musicPosition)}</span>
                              <span>
                                -
                                {formatTime(
                                  Math.max(musicDuration - musicPosition, 0)
                                )}
                              </span>
                            </div>
                          </ProgressContainer>

                          {playlist.length === 0 ? (
                            <EmptyState>No tracks in playlist.</EmptyState>
                          ) : (
                            <PlaylistList>
                              {playlist.map((track, index) => (
                                <PlaylistItem
                                  key={track.id}
                                  $active={index === currentTrackIndex}
                                  onClick={() => handlePlaylistItemClick(index)}
                                >
                                  <div className="info">
                                    <span>{track.name}</span>
                                    <small>
                                      {track.metadata?.artist
                                        ? `${track.metadata.artist} · `
                                        : ""}
                                      {formatTime(track.duration || 0)} ·{" "}
                                      {formatFileSize(track.size)}
                                    </small>
                                  </div>
                                  <PlaylistButtonGroup>
                                    <SmallIconButton
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleRemoveTrack(track.id);
                                      }}
                                    >
                                      <Icon
                                        icon="mdi:close"
                                        width={16}
                                        height={16}
                                      />
                                    </SmallIconButton>
                                  </PlaylistButtonGroup>
                                </PlaylistItem>
                              ))}
                            </PlaylistList>
                          )}
                        </PlaylistWrapper>
                      )}

                      {activeControlPanel === "mixer" && (
                        <MixerGrid>
                          <MixerSection>
                            <MixerSectionHeader>
                              <h3>Microphone</h3>
                              <small>
                                Control how you and listeners hear you
                              </small>
                            </MixerSectionHeader>
                            {!showMicMixer ? (
                              <ControlButton
                                $variant="tertiary"
                                onClick={() => setShowMicMixer(true)}
                              >
                                <Icon
                                  icon="mdi:microphone-settings"
                                  width={18}
                                  height={18}
                                />
                                Show microphone controls
                              </ControlButton>
                            ) : (
                              <>
                                <SliderRow>
                                  <span>Audience volume</span>
                                  <input
                                    type="range"
                                    min={0}
                                    max={2}
                                    step={0.01}
                                    value={micAudienceVolume}
                                    onChange={(event) =>
                                      handleMicAudienceVolumeChange(
                                        event.target.value
                                      )
                                    }
                                  />
                                  <output>
                                    {micAudienceVolume.toFixed(2)}
                                  </output>
                                </SliderRow>
                                <SliderRow>
                                  <span>Monitor volume</span>
                                  <input
                                    type="range"
                                    min={0}
                                    max={2}
                                    step={0.01}
                                    value={micMonitorVolume}
                                    onChange={(event) =>
                                      handleMicMonitorVolumeChange(
                                        event.target.value
                                      )
                                    }
                                    disabled={
                                      !monitoringEnabled || !micMonitorEnabled
                                    }
                                  />
                                  <output>{micMonitorVolume.toFixed(2)}</output>
                                </SliderRow>
                                <ToggleRow>
                                  <input
                                    type="checkbox"
                                    checked={micMonitorEnabled}
                                    onChange={(event) =>
                                      handleMicMonitorToggle(
                                        event.target.checked
                                      )
                                    }
                                  />
                                  <span>Enable self-monitor</span>
                                </ToggleRow>
                                <ToggleRow>
                                  <input
                                    type="checkbox"
                                    checked={!micMuted}
                                    onChange={(event) =>
                                      handleToggleMic(!event.target.checked)
                                    }
                                  />
                                  <span>Send microphone to audience</span>
                                </ToggleRow>
                                <ControlButton
                                  $variant="tertiary"
                                  onClick={() => setShowMicMixer(false)}
                                >
                                  <Icon
                                    icon="mdi:chevron-up"
                                    width={18}
                                    height={18}
                                  />
                                  Hide microphone controls
                                </ControlButton>
                              </>
                            )}
                          </MixerSection>

                          <MixerSection>
                            <MixerSectionHeader>
                              <h3>Music / Media</h3>
                              <small>
                                Control playback for you and listeners
                              </small>
                            </MixerSectionHeader>
                            {!showMusicMixer ? (
                              <ControlButton
                                $variant="tertiary"
                                onClick={() => setShowMusicMixer(true)}
                              >
                                <Icon icon="mdi:music" width={18} height={18} />
                                Show music controls
                              </ControlButton>
                            ) : (
                              <>
                                <SliderRow>
                                  <span>Audience volume</span>
                                  <input
                                    type="range"
                                    min={0}
                                    max={2}
                                    step={0.01}
                                    value={musicAudienceVolume}
                                    onChange={(event) =>
                                      handleMusicAudienceVolumeChange(
                                        event.target.value
                                      )
                                    }
                                    disabled={musicMuted}
                                  />
                                  <output>
                                    {musicAudienceVolume.toFixed(2)}
                                  </output>
                                </SliderRow>
                                <SliderRow>
                                  <span>Monitor volume</span>
                                  <input
                                    type="range"
                                    min={0}
                                    max={2}
                                    step={0.01}
                                    value={musicMonitorVolume}
                                    onChange={(event) =>
                                      handleMusicMonitorVolumeChange(
                                        event.target.value
                                      )
                                    }
                                    disabled={
                                      !monitoringEnabled || !musicMonitorEnabled
                                    }
                                  />
                                  <output>
                                    {musicMonitorVolume.toFixed(2)}
                                  </output>
                                </SliderRow>
                                <ToggleRow>
                                  <input
                                    type="checkbox"
                                    checked={musicMonitorEnabled}
                                    onChange={(event) =>
                                      handleMusicMonitorToggle(
                                        event.target.checked
                                      )
                                    }
                                  />
                                  <span>Hear music locally</span>
                                </ToggleRow>
                                <ToggleRow>
                                  <input
                                    type="checkbox"
                                    checked={!musicMuted}
                                    onChange={(event) =>
                                      handleToggleMusicMute(
                                        !event.target.checked
                                      )
                                    }
                                  />
                                  <span>Send music to audience</span>
                                </ToggleRow>
                                <ControlButton
                                  $variant="tertiary"
                                  onClick={handleToggleMusicMute}
                                >
                                  <Icon
                                    icon={
                                      musicMuted
                                        ? "mdi:volume-mute"
                                        : "mdi:volume-high"
                                    }
                                    width={18}
                                    height={18}
                                  />
                                  {musicMuted ? "Unmute music" : "Mute music"}
                                </ControlButton>
                                <ControlButton
                                  $variant="tertiary"
                                  onClick={() => setShowMusicMixer(false)}
                                >
                                  <Icon
                                    icon="mdi:chevron-up"
                                    width={18}
                                    height={18}
                                  />
                                  Hide music controls
                                </ControlButton>
                              </>
                            )}
                          </MixerSection>

                          <MixerSection>
                            <MixerSectionHeader>
                              <h3>Advanced</h3>
                              <small>Monitoring and ducking</small>
                            </MixerSectionHeader>
                            {!showAdvancedMixer ? (
                              <ControlButton
                                $variant="tertiary"
                                onClick={() => setShowAdvancedMixer(true)}
                              >
                                <Icon
                                  icon="mdi:tune-variant"
                                  width={18}
                                  height={18}
                                />
                                Show advanced controls
                              </ControlButton>
                            ) : (
                              <>
                                <ToggleRow>
                                  <input
                                    type="checkbox"
                                    checked={monitoringEnabled}
                                    onChange={(event) =>
                                      handleMonitoringToggle(
                                        event.target.checked
                                      )
                                    }
                                  />
                                  <span>Enable local monitoring</span>
                                </ToggleRow>
                                <ToggleRow>
                                  <input
                                    type="checkbox"
                                    checked={duckingEnabled}
                                    onChange={(event) =>
                                      handleDuckingToggle(event.target.checked)
                                    }
                                  />
                                  <span>
                                    Ducking (lower music when speaking)
                                  </span>
                                </ToggleRow>
                                <SliderRow>
                                  <span>Ducking strength (audience)</span>
                                  <input
                                    type="range"
                                    min={0}
                                    max={0.6}
                                    step={0.01}
                                    value={duckingStrength}
                                    onChange={(event) =>
                                      handleDuckingStrengthChange(
                                        event.target.value
                                      )
                                    }
                                    disabled={!duckingEnabled}
                                  />
                                  <output>{duckingStrength.toFixed(2)}</output>
                                </SliderRow>
                                <SliderRow>
                                  <span>Ducking strength (monitor)</span>
                                  <input
                                    type="range"
                                    min={0}
                                    max={0.8}
                                    step={0.01}
                                    value={monitorDuckingStrength}
                                    onChange={(event) =>
                                      handleMonitorDuckingStrengthChange(
                                        event.target.value
                                      )
                                    }
                                    disabled={
                                      !duckingEnabled || !monitoringEnabled
                                    }
                                  />
                                  <output>
                                    {monitorDuckingStrength.toFixed(2)}
                                  </output>
                                </SliderRow>
                                <ControlButton
                                  $variant="tertiary"
                                  onClick={() => setShowAdvancedMixer(false)}
                                >
                                  <Icon
                                    icon="mdi:chevron-up"
                                    width={18}
                                    height={18}
                                  />
                                  Hide advanced controls
                                </ControlButton>
                              </>
                            )}
                          </MixerSection>
                        </MixerGrid>
                      )}
                    </ControlContent>
                  </ControlPanel>
                </div>
              )}

              {showParticipants && (
                <div className="controls-column">
                  {/* TABS FOR SWITCHING VIEWS */}
                  <PanelTabs>
                    <PanelActionTab
                      $active={panelTab === "participants"}
                      onClick={() => setPanelTab("participants")}
                    >
                      Participants
                    </PanelActionTab>
                    <PanelActionTab
                      $active={panelTab === "chat"}
                      onClick={() => setPanelTab("chat")}
                    >
                      Chat ({comments?.totalElements || 0})
                    </PanelActionTab>
                  </PanelTabs>

                  {/* PARTICIPANTS VIEW */}
                  {panelTab === "participants" && (
                    <ControlPanel>
                      <ControlHeader>
                        <h2>On Stage</h2>
                        <StatusChip $variant="info">
                          <Icon
                            icon="mdi:account-group"
                            width={18}
                            height={18}
                          />
                          {specificEvent?.data?.cohosts?.length || 0}
                        </StatusChip>
                      </ControlHeader>
                      <p className="meta">Cohosts of the event.</p>

                      <ControlContent>
                        {!specificEvent?.data?.cohosts ||
                        specificEvent.data.cohosts.length === 0 ? (
                          <EmptyState>No cohosts added.</EmptyState>
                        ) : (
                          <ul
                            style={{
                              listStyle: "none",
                              padding: 0,
                              margin: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {specificEvent.data.cohosts.map((cohost) => (
                              <li
                                key={cohost.id || cohost._id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  justifyContent: "space-between",
                                  padding: "8px",
                                  background: "rgba(255, 255, 255, 0.03)",
                                  borderRadius: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {(() => {
                                  const peerKey = getCohostPeerKey(cohost);
                                  const cohostStatus = peerKey
                                    ? cohostLiveState.get(peerKey)
                                    : null;
                                  const isLive = Boolean(cohostStatus);
                                  // const isMuted = Boolean(cohostStatus?.muted);
                                  // const disableMute = !isLive || !peerKey;

                                  return (
                                    <>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "12px",
                                          flex: "1 1 auto",
                                          minWidth: 0,
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "36px",
                                            height: "36px",
                                            borderRadius: "50%",
                                            background: "#232323",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#eff0fa",
                                            border: "1px solid #333",
                                          }}
                                        >
                                          <Icon
                                            icon="mdi:account"
                                            width={20}
                                            height={20}
                                          />
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "2px",
                                            overflow: "hidden",
                                          }}
                                        >
                                          <span
                                            style={{
                                              color: "#eff0fa",
                                              fontSize: "14px",
                                              fontWeight: 500,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {cohost.fname} {cohost.lname}
                                          </span>
                                          <span
                                            style={{
                                              color: "#9ca3af",
                                              fontSize: "12px",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {cohost.email}
                                          </span>
                                        </div>
                                      </div>
                                    </>
                                  );
                                })()}
                              </li>
                            ))}
                          </ul>
                        )}
                      </ControlContent>
                    </ControlPanel>
                  )}

                  {/* CHAT VIEW */}
                  {panelTab === "chat" && (
                    <ControlPanel
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      <ControlHeader>
                        <h2>Comments</h2>
                        <StatusChip $variant="success">Active</StatusChip>
                      </ControlHeader>

                      <ChatContainer
                        ref={chatContainerRef}
                        onScroll={handleChatScroll}
                      >
                        {comments?.content?.map((comment, idx) => (
                          <ChatMessage key={comment.id || idx}>
                            <div className="avatar">
                              {/* Use comment.user details if populated, else fallback */}
                              <img
                                src={
                                  comment.user?.image ||
                                  comment.user?.profileImage ||
                                  "/images/image_1.png"
                                }
                                alt="User"
                              />
                            </div>
                            <div className="content">
                              <div className="sender">
                                {comment.username}
                                <span className="time">
                                  {timeAgo(comment.timestamp)}
                                </span>
                              </div>
                              <div className="text">{comment.text}</div>
                              <ReactionWrapper>
                                <ReactionButton
                                  $active={comment.userReaction === "LIKE"}
                                  onClick={() =>
                                    handleReaction(
                                      comment.id || comment._id,
                                      "LIKE",
                                      comment.userReaction
                                    )
                                  }
                                  title="Like"
                                >
                                  <Icon icon="mdi:thumb-up" width={14} />
                                  {comment.reactionCounts?.LIKE > 0 && (
                                    <span>{comment.reactionCounts.LIKE}</span>
                                  )}
                                </ReactionButton>
                                <ReactionButton
                                  $active={comment.userReaction === "DISLIKE"}
                                  onClick={() =>
                                    handleReaction(
                                      comment.id || comment._id,
                                      "DISLIKE",
                                      comment.userReaction
                                    )
                                  }
                                  title="Dislike"
                                >
                                  <Icon icon="mdi:thumb-down" width={14} />
                                  {comment.reactionCounts?.DISLIKE > 0 && (
                                    <span>
                                      {comment.reactionCounts.DISLIKE}
                                    </span>
                                  )}
                                </ReactionButton>
                                <ReactionButton
                                  $active={comment.userReaction === "LOVE"}
                                  onClick={() =>
                                    handleReaction(
                                      comment.id || comment._id,
                                      "LOVE",
                                      comment.userReaction
                                    )
                                  }
                                  title="Love"
                                >
                                  <Icon icon="mdi:heart" width={14} />
                                  {comment.reactionCounts?.LOVE > 0 && (
                                    <span>{comment.reactionCounts.LOVE}</span>
                                  )}
                                </ReactionButton>
                                <ReactionButton
                                  $active={
                                    comment.userReaction === "INDIFFERENT"
                                  }
                                  onClick={() =>
                                    handleReaction(
                                      comment.id || comment._id,
                                      "INDIFFERENT",
                                      comment.userReaction
                                    )
                                  }
                                  title="Indifferent"
                                >
                                  <Icon
                                    icon="mdi:emoticon-neutral"
                                    width={14}
                                  />
                                  {comment.reactionCounts?.INDIFFERENT > 0 && (
                                    <span>
                                      {comment.reactionCounts.INDIFFERENT}
                                    </span>
                                  )}
                                </ReactionButton>
                                <ReactionButton
                                  onClick={() =>
                                    handleReaction(
                                      comment.id || comment._id,
                                      "NOT_SET",
                                      comment.userReaction
                                    )
                                  }
                                  title="Clear"
                                >
                                  <Icon icon="mdi:close-circle" width={14} />
                                </ReactionButton>
                              </ReactionWrapper>
                            </div>
                          </ChatMessage>
                        ))}
                        {commentsLoading && (
                          <div style={{ textAlign: "center", padding: "10px" }}>
                            <ClipLoader size={20} color="#ed1c24" />
                          </div>
                        )}
                        {!commentsHasMore &&
                          (comments?.content?.length || 0) > 0 && (
                            <EmptyState
                              style={{ textAlign: "center", padding: "10px" }}
                            >
                              End of comments
                            </EmptyState>
                          )}
                        {!commentsLoading &&
                          (comments?.content?.length || 0) === 0 && (
                            <EmptyState>
                              No comments yet. Be the first!
                            </EmptyState>
                          )}
                      </ChatContainer>

                      <ChatInputWrapper>
                        <ChatInput
                          type="text"
                          placeholder="Type a message..."
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSendComment();
                            }
                          }}
                        />
                        <ChatSendButton
                          type="button"
                          disabled={!commentText.trim()}
                          onClick={handleSendComment}
                        >
                          <Icon icon="mdi:send" width={20} height={20} />
                        </ChatSendButton>
                      </ChatInputWrapper>
                    </ControlPanel>
                  )}
                </div>
              )}
            </div>
            <ControlBar>
              <ControlSection>
                <IconButton
                  onClick={() => setShowDevicePicker((prev) => !prev)}
                  title="Toggle microphone source"
                >
                  <Icon icon="mdi:settings-helper" width={22} height={22} />
                </IconButton>
              </ControlSection>

              {isStreaming && (
                <ControlSection className="center">
                  <ControlButton
                    $variant="danger"
                    onClick={handleStopStreaming}
                  >
                    <Icon icon="mdi:power" width={20} height={20} />
                    End Live
                  </ControlButton>
                </ControlSection>
              )}

              <ControlSection className="right">
                <IconButton
                  onClick={() => setShowSharePanel((prev) => !prev)}
                  title="Share stream link"
                  disabled={!shareLink}
                >
                  <Icon icon="mdi:share-variant" width={22} height={22} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    if (showControls) {
                      setShowControls(false);
                    } else {
                      setShowControls(true);
                      setShowParticipants(false);
                    }
                  }}
                  title={showControls ? "Collapse controls" : "Expand controls"}
                >
                  <Icon
                    icon={
                      showControls ? "mdi:chevron-right" : "mdi:chevron-left"
                    }
                    width={22}
                    height={22}
                  />
                </IconButton>

                <IconButton
                  onClick={() => {
                    if (showParticipants) {
                      setShowParticipants(false);
                    } else {
                      setShowParticipants(true);
                      setShowControls(false);
                    }
                  }}
                  title={
                    showParticipants
                      ? "Collapse participants"
                      : "Expand participants"
                  }
                >
                  <Icon
                    icon={
                      showParticipants
                        ? "solar:settings-line-duotone"
                        : "material-symbols:settings-outline-rounded"
                    }
                    width={22}
                    height={22}
                  />
                </IconButton>
              </ControlSection>
            </ControlBar>
            {showSharePanel && shareLink && (
              <SharePanelWrapper>
                <ShareCard>
                  <h4>Share your broadcast</h4>
                  <p>
                    Send this link to listeners so they can tune in. It contains
                    all the info they need to join.
                  </p>
                  <ShareLinkRow>
                    <p>{shareLink}</p>
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      disabled={!shareLink}
                    >
                      <Icon icon="mdi:content-copy" width={16} height={16} />{" "}
                      Copy
                    </button>
                  </ShareLinkRow>
                  {copyFeedback && (
                    <CopyFeedbackNote>{copyFeedback}</CopyFeedbackNote>
                  )}
                </ShareCard>
              </SharePanelWrapper>
            )}
          </div>
        )}

        <div className="down-audio">
          <div>
            <p>{status}</p>
            <div className="down-audio-middle">
              <Icon icon="mdi:clock-outline" width={16} height={16} />
              <span>
                {isStreaming
                  ? "Live broadcast in progress"
                  : "Waiting to go live"}
              </span>
            </div>
          </div>
          <div className="down-audio-middle">
            <Icon icon="mdi:account-circle" width={16} height={16} />
            <span>{hostUser?.fname || "Host"}</span>
          </div>
        </div>
      </div>
    </GoLiveShell>
  );
};

export default GoLive;

const PanelTabs = styled.div`
  display: flex;
  align-items: center;
  background: #232323;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 16px;
`;

const PanelActionTab = styled.button`
  flex: 1;
  border: none;
  background: ${({ $active }) => ($active ? "#3d3d3d" : "transparent")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#8f9099")};
  font-size: 13px;
  font-weight: 600;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 300px;
  max-height: 400px; /* Limit height to allow scrolling within panel */

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #444;
    border-radius: 4px;
  }
`;

const ChatMessage = styled.div`
  display: flex;
  gap: 12px;
  font-size: 14px;

  .avatar {
    width: 32px;
    height: 32px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .sender {
    font-weight: 600;
    color: #eff0fa;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .time {
    font-size: 11px;
    color: #8f9099;
    font-weight: 400;
  }

  .text {
    color: #d1d5db;
    word-break: break-word;
    line-height: 1.4;
  }
`;

const ChatInputWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
  border-top: 1px solid #333;
  padding-top: 16px;
`;

const ChatInput = styled.input`
  flex: 1;
  background: #232323;
  border: 1px solid #3d3d3d;
  border-radius: 20px;
  padding: 10px 16px;
  color: #eff0fa;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #ed1c24;
  }

  &::placeholder {
    color: #6b7280;
  }
`;

const ChatSendButton = styled.button`
  background: #ed1c24;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: background 0.2s;

  &:hover {
    background: #c9181f;
  }

  &:disabled {
    background: #444;
    cursor: not-allowed;
    color: #888;
  }
`;

const ReactionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const ReactionButton = styled.button`
  background: ${({ $active }) =>
    $active ? "rgba(237, 28, 36, 0.15)" : "#222"};
  border: 1px solid ${({ $active }) => ($active ? "#ed1c24" : "#333")};
  color: ${({ $active }) => ($active ? "#ed1c24" : "#8f9099")};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #ed1c24;
    color: #ed1c24;
  }

  span {
    font-weight: 600;
  }
`;
